import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, MapPin, Building2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCities() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/admin/cities/stats');
        setStats(res.data);
      } catch {
        toast.error('Erro ao carregar dados das cidades');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [api]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/admin/cities/download-excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cidades_brasil.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download concluido!');
    } catch {
      toast.error('Erro ao gerar o arquivo Excel');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-6 transition-colors" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Painel
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Cidades do Brasil</h1>
            <p className="text-sm text-slate-500">Repositorio completo de cidades e estados</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="total-cities-card">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-slate-500">Total de Cidades</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">{stats?.total_cities?.toLocaleString('pt-BR')}</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="total-states-card">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-slate-500">Estados</span>
                </div>
                <p className="text-3xl font-bold text-slate-800">{stats?.total_states}</p>
              </div>
            </div>

            {/* Download Button */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8" data-testid="download-section">
              <h2 className="font-semibold text-lg text-slate-800 mb-2">Download Excel</h2>
              <p className="text-sm text-slate-500 mb-4">
                Arquivo com todas as {stats?.total_cities?.toLocaleString('pt-BR')} cidades organizadas por estado. 
                Colunas: <strong>Cidade</strong> e <strong>Estado</strong>.
              </p>
              <Button onClick={handleDownload} disabled={downloading} className="gap-2" data-testid="download-excel-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Gerando arquivo...' : 'Baixar cidades_brasil.xlsx'}
              </Button>
            </div>

            {/* States breakdown */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="states-breakdown">
              <h2 className="font-semibold text-lg text-slate-800 mb-4">Cidades por Estado</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {stats?.states?.map((s) => (
                  <div key={s.uf} className="text-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-sm font-bold text-slate-700">{s.uf}</p>
                    <p className="text-xs text-slate-500">{s.count} cidades</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
